/**
 * SyncConfigurationCard Component
 *
 * Displays and manages all sync configuration settings:
 * - Enable/disable sync
 * - Sync interval
 * - Sync triggers (on session close, on login)
 */

// 1. External dependencies
import React from 'react';
import { Settings, Info } from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardHeader,
  CardBody,
  Switch,
  Select,
  SelectItem
} from '@enfomentis/ui';
import type { SyncSettings } from '@/types/sync/SyncSettings';

export interface SyncConfigurationCardProps {
  /** Current sync settings */
  settings: SyncSettings | null;
  /** Callback when settings change */
  onChange: (updates: Partial<SyncSettings>) => Promise<void>;
  /** Whether a provider is connected */
  isConnected: boolean;
}

/**
 * Sync Configuration Card
 *
 * Provides UI for all sync settings configuration.
 * Calls parent onChange with partial updates.
 */
export const SyncConfigurationCard: React.FC<SyncConfigurationCardProps> = ({
  settings,
  onChange,
  isConnected
}) => {
  const handleSyncToggle = async (enabled: boolean): Promise<void> => {
    await onChange({ syncEnabled: enabled });
  };

  const handleIntervalChange = (keys: 'all' | Set<React.Key>): void => {
    if (keys === 'all') return;
    const interval = Number(Array.from(keys)[0]);
    if (interval) {
      void onChange({ syncInterval: interval });
    }
  };

  const handleSessionCloseToggle = async (enabled: boolean): Promise<void> => {
    await onChange({ syncOnSessionClose: enabled });
  };

  const handleLoginToggle = async (enabled: boolean): Promise<void> => {
    await onChange({ syncOnLogin: enabled });
  };

  if (!settings) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <Settings className="w-5 h-5 text-text-secondary" />
        <h3 className="text-lg font-semibold">Sync Configuration</h3>
      </CardHeader>
      <CardBody className="space-y-6 p-4 sm:p-6">
        {/* Not Connected Info */}
        {!isConnected && (
          <div className="flex items-start gap-3 p-4 bg-background-secondary rounded-lg border border-border">
            <Info className="w-5 h-5 text-text-tertiary flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-text-secondary break-words">
                Connect a sync location to configure these settings
              </p>
              <p className="text-xs text-text-tertiary mt-1 break-words">
                Use the Storage Location card above to connect to a local directory.
              </p>
            </div>
          </div>
        )}

        {/* Enable/Disable Sync */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="font-medium break-words">Enable Automatic Sync</p>
            <p className="text-sm text-text-secondary break-words">
              Automatically sync your data at regular intervals
            </p>
          </div>
          <Switch
            isSelected={settings.syncEnabled}
            onValueChange={handleSyncToggle}
            isDisabled={!isConnected}
            aria-label="Enable automatic sync"
            className="flex-shrink-0"
          />
        </div>

        {/* Sync Interval */}
        <Select
          label="Sync Interval"
          placeholder="Select sync frequency"
          selectedKeys={settings.syncInterval ? [String(settings.syncInterval)] : []}
          onSelectionChange={handleIntervalChange}
          isDisabled={!settings.syncEnabled || !isConnected}
          description="How often to automatically sync your data"
          size="lg"
          className="w-full"
        >
          <SelectItem key="1">Every 1 minute</SelectItem>
          <SelectItem key="5">Every 5 minutes</SelectItem>
          <SelectItem key="10">Every 10 minutes</SelectItem>
          <SelectItem key="15">Every 15 minutes</SelectItem>
          <SelectItem key="30">Every 30 minutes</SelectItem>
        </Select>

        {/* Warn on Unsaved Changes */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="font-medium break-words">Warn on Unsaved Changes</p>
            <p className="text-sm text-text-secondary break-words">
              Show warning when closing with unsynced data
            </p>
          </div>
          <Switch
            isSelected={settings.syncOnSessionClose}
            onValueChange={handleSessionCloseToggle}
            isDisabled={!isConnected}
            aria-label="Warn on unsaved changes"
            className="flex-shrink-0"
          />
        </div>

        {/* Sync on Login */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="font-medium break-words">Sync on Login</p>
            <p className="text-sm text-text-secondary break-words">
              Automatically sync when you log in
            </p>
          </div>
          <Switch
            isSelected={settings.syncOnLogin}
            onValueChange={handleLoginToggle}
            isDisabled={!isConnected}
            aria-label="Sync on login"
            className="flex-shrink-0"
          />
        </div>
      </CardBody>
    </Card>
  );
};
